/**
 * Role Impersonation Badge Component
 * Displays current role status and provides role switching functionality
 */

import React, { useState, useEffect } from "react";
import {
  Badge,
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Select,
  Text,
  useDisclosure,
  useToast,
  VStack,
  Tooltip,
} from "@chakra-ui/react";
import { FaUserSecret, FaUserShield, FaTimes } from "react-icons/fa";
import roleImpersonationService from "../services/roleImpersonationService";

const RoleImpersonationBadge = () => {
  const [status, setStatus] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [loading, setLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    console.log("RoleImpersonationBadge: Component mounted, loading data...");
    loadStatus();
    loadAvailableRoles();
  }, []);

  const loadStatus = async () => {
    try {
      const response = await roleImpersonationService.getStatus();
      console.log("Role Impersonation Status Response:", response.data);
      setStatus(response.data.data); // Access nested data object
      console.log("Role Impersonation Status:", response.data.data);
    } catch (error) {
      console.error("Failed to load impersonation status:", error);
      console.error("Error details:", error.response?.data);
    }
  };

  const loadAvailableRoles = async () => {
    try {
      const response = await roleImpersonationService.getAvailableRoles();
      console.log("Available Roles Response:", response.data);
      setAvailableRoles(response.data.data.roles || []); // Access nested data.roles
      console.log("Available Roles:", response.data.data.roles);
    } catch (error) {
      console.error("Failed to load available roles:", error);
      console.error("Error details:", error.response?.data);
    }
  };

  const handleStartImpersonation = async () => {
    if (!selectedRole) {
      toast({
        title: "Please select a role",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      await roleImpersonationService.startImpersonation(selectedRole);

      toast({
        title: "Role Impersonation Active",
        description: `Now acting as: ${selectedRole}`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      // Reload the page to apply new role permissions
      window.location.reload();
    } catch (error) {
      toast({
        title: "Failed to start impersonation",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const handleExitImpersonation = async () => {
    setLoading(true);
    try {
      await roleImpersonationService.exitImpersonation();

      toast({
        title: "Exited Impersonation",
        description: "Returned to your original role",
        status: "info",
        duration: 3000,
        isClosable: true,
      });

      // Reload the page to apply original role permissions
      window.location.reload();
    } catch (error) {
      toast({
        title: "Failed to exit impersonation",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Don't render if user can't impersonate
  if (!status || !status.canImpersonate) {
    console.log("RoleImpersonationBadge: Not rendering. Status:", status);
    return null;
  }

  console.log("RoleImpersonationBadge: Rendering with status:", status);

  return (
    <>
      {/* Current Status Badge */}
      {status.isImpersonating ? (
        <HStack
          spacing={2}
          bg="orange.500"
          color="white"
          px={3}
          py={1}
          borderRadius="md"
          fontSize="sm"
          fontWeight="medium"
        >
          <Icon as={FaUserSecret} />
          <Text>Mock: {status.currentRole}</Text>
          <Tooltip label="Exit Mock Mode">
            <IconButton
              icon={<FaTimes />}
              size="xs"
              variant="ghost"
              colorScheme="whiteAlpha"
              aria-label="Exit mock mode"
              onClick={handleExitImpersonation}
              isLoading={loading}
            />
          </Tooltip>
        </HStack>
      ) : (
        <Menu>
          <Tooltip label="Mock Role (Testing)">
            <MenuButton
              as={Badge}
              colorScheme="purple"
              variant="subtle"
              px={3}
              py={1}
              borderRadius="md"
              cursor="pointer"
              fontSize="sm"
              _hover={{ bg: "purple.100" }}
            >
              <HStack spacing={2}>
                <Icon as={FaUserShield} />
                <Text>{status.currentRole}</Text>
              </HStack>
            </MenuButton>
          </Tooltip>
          <MenuList>
            <MenuItem onClick={onOpen}>
              <HStack spacing={2}>
                <Icon as={FaUserSecret} />
                <Text>Mock Role</Text>
              </HStack>
            </MenuItem>
          </MenuList>
        </Menu>
      )}

      {/* Role Selection Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Mock Role for Testing</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <Text fontSize="sm" color="gray.600" mb={3}>
                  Temporarily assume a different role to test functionality.
                  Your original role ({status?.originalRole}) will be restored
                  when you exit.
                </Text>
              </Box>

              <Box
                p={3}
                bg="orange.50"
                borderLeft="4px solid"
                borderColor="orange.400"
                borderRadius="md"
              >
                <HStack spacing={2} mb={1}>
                  <Icon as={FaUserSecret} color="orange.600" />
                  <Text fontSize="sm" fontWeight="bold" color="orange.700">
                    Testing Mode Only
                  </Text>
                </HStack>
                <Text fontSize="xs" color="orange.600">
                  This feature is for testing purposes. All actions will still
                  be associated with your account.
                </Text>
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  Select Role to Mock:
                </Text>
                <Select
                  placeholder="Choose a role..."
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  size="lg"
                >
                  {availableRoles.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label} - {role.description}
                    </option>
                  ))}
                </Select>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="orange"
              onClick={handleStartImpersonation}
              isLoading={loading}
              isDisabled={!selectedRole}
            >
              Start Mock Mode
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default RoleImpersonationBadge;
